import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { label } from "../../lib/format";
import { Card, PageHeader } from "../../components/common/UI";
import { Button } from "../../components/ui/button";

const KINDS = {
  session: ["Session", "bg-sky-600"], hearing: ["Hearing", "bg-violet-600"], executive_deadline: ["Executive deadline", "bg-amber-500"],
  review_deadline: ["Review deadline", "bg-orange-600"], effectivity: ["Effectivity", "bg-emerald-600"], posting_end: ["Posting ends", "bg-slate-500"],
};
const MANILA = 8 * 3600000;
const iso = (d) => d.toISOString().slice(0, 10);
const dayKey = (e) => (e.all_day ? e.at : iso(new Date(new Date(/Z|[+-]\d\d:\d\d$/.test(e.at) ? e.at : `${e.at}Z`).getTime() + MANILA)));
const timeOf = (e) => (e.all_day ? "All day" : new Date(/Z$/.test(e.at) ? e.at : `${e.at}Z`).toLocaleTimeString("en-PH", { timeZone: "Asia/Manila", hour: "numeric", minute: "2-digit" }));

function gridDays(y, m) {
  const first = new Date(Date.UTC(y, m, 1));
  const start = new Date(first.getTime() - first.getUTCDay() * 86400000);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * 86400000));
}

function EventChip({ e, compact }) {
  const [name, color] = KINDS[e.kind];
  const lvl = e.level === "overdue" ? "ring-2 ring-red-500" : e.level === "due_soon" ? "ring-2 ring-amber-400" : "";
  const done = e.kind.endsWith("_deadline") && e.status !== "pending";
  return (
    <Link to={e.link} onClick={(ev) => ev.stopPropagation()} className={`block truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white ${color} ${lvl} ${done ? "opacity-40 line-through" : ""}`} title={`${name}: ${e.title}${e.measure_ref ? ` · ${e.measure_ref}` : ""}`}
      data-testid={`cal-event-${e.kind}`}>
      {compact ? (e.measure_ref || e.title) : `${timeOf(e)} · ${e.title}`}
    </Link>
  );
}

function DayList({ day, events }) {
  return (
    <Card title={new Date(`${day}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", weekday: "long", month: "long", day: "numeric", year: "numeric" })} testId="cal-day-panel">
      {events.length === 0 && <div className="text-xs text-slate-500">Nothing scheduled.</div>}
      <div className="space-y-2">
        {events.map((e, i) => (
          <Link key={i} to={e.link} className="block rounded border border-slate-200 p-2 text-xs hover:bg-slate-50" data-testid={`cal-day-item-${i}`}>
            <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${KINDS[e.kind][1]}`} /><b>{KINDS[e.kind][0]}</b><span className="text-slate-500">{timeOf(e)}</span>
              {e.level === "overdue" && <span className="rounded bg-red-100 px-1 text-[10px] font-semibold text-red-700">Overdue</span>}
              {e.status && <span className="ml-auto text-[10px] uppercase text-slate-400">{label(e.status)}</span>}</div>
            <div className="mt-1 text-slate-800">{e.title}</div>
            {e.measure_ref && <div className="text-[11px] text-slate-500">{e.measure_ref} — {e.measure_title}</div>}
            {e.venue && <div className="text-[11px] text-slate-500">{e.venue}</div>}
          </Link>
        ))}
      </div>
    </Card>
  );
}

export default function LegislativeCalendar() {
  const now = new Date(Date.now() + MANILA);
  const [ym, setYm] = useState([now.getUTCFullYear(), now.getUTCMonth()]);
  const [data, setData] = useState({ events: [], today: iso(now) });
  const [hidden, setHidden] = useState({});
  const [sel, setSel] = useState(iso(now));
  const days = useMemo(() => gridDays(...ym), [ym]);
  useEffect(() => {
    api.get("/calendar", { params: { start: iso(days[0]), end: iso(days[41]) } }).then((r) => setData(r.data));
  }, [days]);
  const byDay = useMemo(() => {
    const g = {};
    data.events.filter((e) => !hidden[e.kind]).forEach((e) => { (g[dayKey(e)] ||= []).push(e); });
    return g;
  }, [data, hidden]);
  const move = (d) => setYm(([y, m]) => [m + d < 0 ? y - 1 : m + d > 11 ? y + 1 : y, (m + d + 12) % 12]);
  const title = new Date(Date.UTC(ym[0], ym[1], 1)).toLocaleDateString("en-PH", { timeZone: "UTC", month: "long", year: "numeric" });
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Legislative calendar" title={title} subtitle="Sessions, committee hearings and post-approval deadlines in one view (Asia/Manila). You only see items for measures you are allowed to view."
        actions={<div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => { setYm([now.getUTCFullYear(), now.getUTCMonth()]); setSel(iso(now)); }} data-testid="cal-today">Today</Button>
          <Button size="sm" variant="outline" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="cal-legend">
        {Object.entries(KINDS).map(([k, [n, c]]) => (
          <button key={k} onClick={() => setHidden({ ...hidden, [k]: !hidden[k] })} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${hidden[k] ? "border-slate-200 text-slate-400 line-through" : "border-slate-300 text-slate-700"}`} data-testid={`cal-filter-${k}`}>
            <span className={`h-2 w-2 rounded-full ${c}`} />{n}
          </button>
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="min-w-0 overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm" data-testid="cal-grid">
          <div className="grid grid-cols-7 border-b bg-slate-50 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const k = iso(d);
              const ev = byDay[k] || [];
              const out = d.getUTCMonth() !== ym[1];
              return (
                <div key={k} onClick={() => setSel(k)} role="button" data-testid={`cal-day-${k}`}
                  className={`min-h-[64px] cursor-pointer border-b border-r p-1 transition-colors md:min-h-[104px] ${out ? "bg-slate-50/60 text-slate-400" : ""} ${sel === k ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                  <div className={`mb-1 text-[11px] font-semibold ${k === data.today ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-sky-600 text-white" : ""}`}>{d.getUTCDate()}</div>
                  <div className="hidden space-y-0.5 md:block">
                    {ev.slice(0, 3).map((e, i) => <EventChip key={i} e={e} compact={e.all_day} />)}
                    {ev.length > 3 && <div className="text-[10px] text-slate-500">+{ev.length - 3} more</div>}
                  </div>
                  {ev.length > 0 && <div className="flex flex-wrap gap-0.5 md:hidden">{ev.slice(0, 4).map((e, i) => <span key={i} className={`h-1.5 w-1.5 rounded-full ${KINDS[e.kind][1]}`} />)}</div>}
                </div>
              );
            })}
          </div>
        </div>
        <DayList day={sel} events={byDay[sel] || []} />
      </div>
    </div>
  );
}
